import { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Check, 
  X, 
  FileText, 
  Calculator, 
  TrendingUp, 
  Coins,
  Upload,
  CheckSquare,
  BarChart3,
  Clock,
  ChevronRight
} from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HUBSPOT_MEETING_LINK = 'https://meetings-eu1.hubspot.com/josh-dowlman';

// Hero Section
const HeroSection = () => (
  <section data-testid="hero-section" className="min-h-screen bg-parchment section-padding flex items-center">
    <div className="max-w-7xl mx-auto w-full">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left Content */}
        <div className="animate-fade-in">
          <h1 className="heading-xl text-obsidian mb-6">
            Structured accounting for modern digital businesses and creators.
          </h1>
          <p className="body-lg mb-8 max-w-xl">
            Proactive tax strategy, financial systems, and reporting built for ecommerce brands, creators, and Web3 founders.
          </p>
          <div className="flex flex-col sm:flex-row gap-4">
            <a 
              href={HUBSPOT_MEETING_LINK}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-primary inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-primary"
            >
              Book a Strategic Call
              <ArrowRight size={16} strokeWidth={1.5} />
            </a>
            <a 
              href="#services" 
              className="btn-secondary inline-flex items-center justify-center gap-2"
              data-testid="hero-cta-secondary"
            >
              Explore Services
            </a>
          </div>
        </div>
        
        {/* Right Content - Mockup UI */}
        <div className="animate-fade-in-delay-1">
          <div className="space-y-4">
            {/* Profit Dashboard Mockup */}
            <div className="bg-white p-6 border border-iron-grey card-shadow">
              <div className="flex items-center justify-between mb-4">
                <span className="font-inter text-sm uppercase tracking-wider text-iron-grey">Profit Overview</span>
                <span className="font-inter text-xs text-iron-grey">Q4 2024</span>
              </div>
              <div className="h-32 flex items-end gap-2">
                {[40, 55, 45, 60, 75, 65, 80, 70, 85, 90, 78, 95].map((h, i) => (
                  <div key={i} className="flex-1 bg-obsidian" style={{ height: `${h}%` }} />
                ))}
              </div>
              <div className="mt-4 flex justify-between text-xs font-inter text-iron-grey">
                <span>Jan</span>
                <span>Jun</span>
                <span>Dec</span>
              </div>
            </div>
            
            {/* Tax Liability Summary */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-white p-4 border border-iron-grey card-shadow">
                <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Tax Liability</span>
                <p className="font-fraunces font-bold text-2xl text-obsidian mt-1">£42,850</p>
                <span className="font-inter text-xs text-iron-grey">Est. Q4</span>
              </div>
              <div className="bg-white p-4 border border-iron-grey card-shadow">
                <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Saved YTD</span>
                <p className="font-fraunces font-bold text-2xl text-obsidian mt-1">£18,240</p>
                <span className="font-inter text-xs text-iron-grey">Through planning</span>
              </div>
            </div>
            
            {/* Checklist Interface */}
            <div className="bg-white p-4 border border-iron-grey card-shadow">
              <span className="font-inter text-xs uppercase tracking-wider text-iron-grey mb-3 block">Upload Checklist</span>
              <div className="space-y-2">
                {['Bank statements', 'Sales reports', 'Expense receipts'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className={`w-4 h-4 border ${i < 2 ? 'bg-obsidian border-obsidian' : 'border-iron-grey'} flex items-center justify-center`}>
                      {i < 2 && <Check size={12} className="text-parchment" strokeWidth={2} />}
                    </div>
                    <span className={`font-inter text-sm ${i < 2 ? 'line-through text-iron-grey' : 'text-obsidian'}`}>{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Positioning Strip
const PositioningStrip = () => (
  <section data-testid="positioning-strip" className="bg-parchment border-y border-iron-grey py-8">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
      <p className="text-center font-inter text-sm uppercase tracking-widest text-iron-grey mb-6">
        Built for digital operators.
      </p>
      <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-0">
        <span className="font-inter font-medium text-obsidian uppercase tracking-wider text-sm">
          Ecommerce Founders
        </span>
        <span className="hidden md:block w-px h-4 bg-iron-grey mx-8" />
        <span className="font-inter font-medium text-obsidian uppercase tracking-wider text-sm">
          Digital Creators
        </span>
        <span className="hidden md:block w-px h-4 bg-iron-grey mx-8" />
        <span className="font-inter font-medium text-obsidian uppercase tracking-wider text-sm">
          Crypto & Web3 Builders
        </span>
      </div>
    </div>
  </section>
);

// Differentiation Section
const DifferentiationSection = () => (
  <section data-testid="differentiation-section" className="bg-parchment section-padding">
    <div className="max-w-7xl mx-auto">
      <h2 className="heading-lg text-obsidian mb-12 text-center lg:text-left">
        Most firms react. We build structure.
      </h2>
      
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-0 border border-iron-grey">
        {/* Traditional Firms */}
        <div className="p-8 lg:p-12 border-b lg:border-b-0 lg:border-r border-iron-grey">
          <h3 className="font-inter font-medium text-sm uppercase tracking-wider text-iron-grey mb-8">
            Traditional Firms
          </h3>
          <ul className="space-y-4">
            {[
              'Year-end panic',
              'Generic bookkeeping',
              'Confusing document requests',
              'Slow communication',
              'Compliance only'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <X size={16} className="text-iron-grey" strokeWidth={1.5} />
                <span className="font-inter text-iron-grey">{item}</span>
              </li>
            ))}
          </ul>
        </div>
        
        {/* Oakmere Advisory */}
        <div className="p-8 lg:p-12 bg-white">
          <h3 className="font-inter font-medium text-sm uppercase tracking-wider text-obsidian mb-8">
            Oakmere Advisory
          </h3>
          <ul className="space-y-4">
            {[
              'Proactive tax architecture',
              'Structured onboarding checklist',
              'Digital-native accounting systems',
              'Real-time reporting',
              'Informative dashboard of your finances',
              'Strategic financial guidance'
            ].map((item, i) => (
              <li key={i} className="flex items-center gap-3">
                <Check size={16} className="text-obsidian" strokeWidth={1.5} />
                <span className="font-inter text-obsidian">{item}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  </section>
);

// Services Section
const ServicesSection = () => {
  const services = [
    {
      icon: FileText,
      title: 'Accounting Systems & Reporting',
      description: 'Cloud bookkeeping, management reports, financial visibility, year end and monthly financials.',
      slug: 'accounting-systems'
    },
    {
      icon: Calculator,
      title: 'Tax Planning & Compliance',
      description: 'Strategic tax structuring, filings, cross-border considerations.',
      slug: 'tax-planning'
    },
    {
      icon: TrendingUp,
      title: 'Growth & Profit Advisory',
      description: 'Cash flow strategy, margin analysis, scaling decisions.',
      slug: 'growth-advisory'
    },
    {
      icon: Coins,
      title: 'Digital Asset & Crypto Accounting',
      description: 'Wallet reconciliation, transaction tracking, regulatory reporting.',
      slug: 'crypto-accounting'
    }
  ];

  return (
    <section id="services" data-testid="services-section" className="bg-parchment section-padding">
      <div className="max-w-7xl mx-auto">
        <h2 className="heading-lg text-obsidian mb-12">Our Services</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0 border border-iron-grey">
          {services.map((service, i) => (
            <div 
              key={service.slug} 
              className={`p-8 bg-white card-shadow-hover ${
                i < 2 ? 'border-b border-iron-grey' : ''
              } ${i % 2 === 0 ? 'md:border-r border-iron-grey' : ''}`}
              data-testid={`service-card-${service.slug}`}
            >
              <service.icon size={24} className="text-obsidian mb-4" strokeWidth={1.5} />
              <h3 className="heading-sm text-obsidian mb-3">{service.title}</h3>
              <p className="body-md mb-4">{service.description}</p>
              <Link 
                to={`/services/${service.slug}`} 
                className="font-inter text-sm text-obsidian link-underline inline-flex items-center gap-1"
                data-testid={`service-link-${service.slug}`}
              >
                Learn More <ChevronRight size={14} strokeWidth={1.5} />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Client Experience Section
const ClientExperienceSection = () => (
  <section data-testid="client-experience-section" className="bg-parchment section-padding">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
        {/* Left - Visual Mockups */}
        <div className="space-y-4">
          <div className="bg-white p-6 border border-iron-grey card-shadow">
            <div className="flex items-center gap-3 mb-4">
              <Upload size={20} className="text-obsidian" strokeWidth={1.5} />
              <span className="font-inter text-sm uppercase tracking-wider text-obsidian">Secure Document Upload</span>
            </div>
            <div className="border-2 border-dashed border-iron-grey p-8 text-center">
              <p className="font-inter text-sm text-iron-grey">Drag & drop or click to upload</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 border border-iron-grey card-shadow">
              <CheckSquare size={18} className="text-obsidian mb-2" strokeWidth={1.5} />
              <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">What To Send</span>
              <div className="mt-2 space-y-1">
                {['VAT returns', 'Payroll data', 'Invoices'].map((item, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div className="w-3 h-3 border border-obsidian" />
                    <span className="font-inter text-xs text-obsidian">{item}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-white p-4 border border-iron-grey card-shadow">
              <BarChart3 size={18} className="text-obsidian mb-2" strokeWidth={1.5} />
              <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Monthly Report</span>
              <div className="mt-2 h-16 flex items-end gap-1">
                {[30, 45, 35, 55, 40, 60].map((h, i) => (
                  <div key={i} className="flex-1 bg-obsidian" style={{ height: `${h}%` }} />
                ))}
              </div>
            </div>
          </div>
          
          <div className="bg-white p-4 border border-iron-grey card-shadow">
            <div className="flex items-center gap-3 mb-3">
              <Clock size={18} className="text-obsidian" strokeWidth={1.5} />
              <span className="font-inter text-xs uppercase tracking-wider text-iron-grey">Deadline Tracker</span>
            </div>
            <div className="space-y-2">
              {[
                { task: 'Q4 VAT Return', date: 'Jan 7', urgent: true },
                { task: 'Corporation Tax', date: 'Jan 31', urgent: false }
              ].map((item, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-iron-grey last:border-0">
                  <span className="font-inter text-sm text-obsidian">{item.task}</span>
                  <span className={`font-inter text-xs ${item.urgent ? 'text-obsidian font-medium' : 'text-iron-grey'}`}>
                    {item.date}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>
        
        {/* Right - Text */}
        <div>
          <h2 className="heading-lg text-obsidian mb-6">
            Clarity. Structure. Control.
          </h2>
          <p className="body-lg mb-8">
            You will always know what to send, when to send it, and what it means.
          </p>
          <p className="body-md">
            Our client portal provides a structured environment where document management becomes effortless. 
            Clear checklists, deadline tracking, and real-time status updates keep you informed at every step.
          </p>
        </div>
      </div>
    </div>
  </section>
);

// Process Section
const ProcessSection = () => {
  const steps = [
    { num: '01', title: 'Assess', desc: 'Review your current financial position and identify opportunities.' },
    { num: '02', title: 'Structure', desc: 'Design systems and processes tailored to your business.' },
    { num: '03', title: 'Implement', desc: 'Deploy accounting infrastructure and reporting frameworks.' },
    { num: '04', title: 'Monitor', desc: 'Ongoing oversight, optimization, and strategic guidance.' }
  ];

  return (
    <section data-testid="process-section" className="bg-parchment section-padding">
      <div className="max-w-7xl mx-auto">
        <h2 className="heading-lg text-obsidian mb-12">Our Approach</h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-0 border border-iron-grey">
          {steps.map((step, i) => (
            <div 
              key={step.num} 
              className={`p-8 bg-white ${i < 3 ? 'lg:border-r border-iron-grey' : ''} ${i < 2 ? 'md:border-b lg:border-b-0 border-iron-grey' : ''} ${i === 2 ? 'md:border-r-0 lg:border-r border-iron-grey' : ''}`}
              data-testid={`process-step-${step.num}`}
            >
              <span className="font-inter text-xs text-iron-grey mb-2 block">{step.num}</span>
              <h3 className="heading-sm text-obsidian mb-3">{step.title}</h3>
              <p className="body-md">{step.desc}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = () => {
  const tiers = [
    {
      name: 'Starter',
      target: 'For growing businesses getting structured',
      turnover: '£100k - £200k turnover',
      features: [
        'Monthly bookkeeping & reconciliation',
        'Cloud accounting setup (Xero/QBO)',
        'Quarterly management reports',
        'Corporation tax or Self Assessment filing',
        'Tax return preparation & filing',
        'Annual cash flow review',
        'Crypto accounting included'
      ],
      price: '£395',
      period: '/month'
    },
    {
      name: 'Growth',
      target: 'For ambitious founders scaling up',
      turnover: '£200k - £350k turnover',
      features: [
        'Monthly bookkeeping & reconciliation',
        'Monthly management reports',
        'Proactive tax planning & structuring',
        'Director Self Assessment filing',
        'Quarterly cash flow strategy session',
        'Advisory calls (2x/month)',
        'Crypto accounting (up to 200 txns/mo)'
      ],
      price: '£795',
      period: '/month',
      featured: true,
      badge: 'Most Popular'
    },
    {
      name: 'Scale',
      target: 'For established businesses with complexity',
      turnover: '£350k - £500k+ turnover',
      features: [
        'Monthly reports + KPI dashboard',
        'Advanced tax planning & structuring',
        'Monthly growth strategy sessions',
        'Unlimited advisory calls',
        'Full margin, pricing & profit analysis',
        'Crypto accounting (unlimited txns)',
        'Dedicated account manager'
      ],
      price: '£1,295',
      period: '/month'
    }
  ];

  const hubspotLink = 'https://meetings-eu1.hubspot.com/josh-dowlman';

  return (
    <section id="pricing" data-testid="pricing-section" className="bg-parchment section-padding">
      <div className="max-w-7xl mx-auto">
        <h2 className="heading-lg text-obsidian mb-4 text-center">Pricing</h2>
        <p className="font-inter text-center text-iron-grey mb-12">All prices exclude tax. Bespoke quotes available.</p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-0 border border-iron-grey">
          {tiers.map((tier, i) => (
            <div 
              key={tier.name} 
              className={`p-8 ${tier.featured ? 'bg-white card-shadow relative z-10' : 'bg-parchment'} ${i < 2 ? 'md:border-r border-iron-grey' : ''}`}
              data-testid={`pricing-tier-${tier.name.toLowerCase()}`}
            >
              {tier.badge && (
                <span className="inline-block bg-obsidian text-parchment text-xs font-inter uppercase tracking-wider px-3 py-1 mb-4">
                  {tier.badge}
                </span>
              )}
              <h3 className="heading-sm text-obsidian mb-2">{tier.name}</h3>
              <p className="font-inter text-sm text-iron-grey mb-1">{tier.target}</p>
              <p className="font-inter text-xs text-iron-grey mb-6">{tier.turnover}</p>
              
              <div className="mb-6">
                <span className="font-inter text-sm text-iron-grey">Starting at </span>
                <span className="font-fraunces font-bold text-4xl text-obsidian">{tier.price}</span>
                <span className="font-inter text-sm text-iron-grey">{tier.period}</span>
              </div>
              
              <ul className="space-y-3 mb-8">
                {tier.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2">
                    <Check size={14} className="text-obsidian flex-shrink-0 mt-1" strokeWidth={1.5} />
                    <span className="font-inter text-sm text-obsidian">{feature}</span>
                  </li>
                ))}
              </ul>
              
              <a 
                href={hubspotLink}
                target="_blank"
                rel="noopener noreferrer"
                className={`${tier.featured ? 'btn-primary' : 'btn-secondary'} w-full text-center block`}
                data-testid={`pricing-cta-${tier.name.toLowerCase()}`}
              >
                Book a Discovery Call
              </a>
            </div>
          ))}
        </div>
        
        {/* Add-ons section */}
        <div className="mt-12 p-8 bg-white border border-iron-grey">
          <h3 className="heading-sm text-obsidian mb-6">Add-Ons</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-md">
            {[
              { name: 'Company Formation', price: '£195' },
              { name: 'CFO Advisory Day', price: '£550' }
            ].map((addon) => (
              <div key={addon.name} className="text-center p-4 bg-parchment">
                <p className="font-inter text-xs text-iron-grey mb-1">{addon.name}</p>
                <p className="font-fraunces font-bold text-obsidian">{addon.price}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

// Authority Section
const AuthoritySection = () => (
  <section data-testid="authority-section" className="bg-parchment section-padding">
    <div className="max-w-3xl mx-auto text-center">
      <h2 className="heading-lg text-obsidian mb-6">
        Financial discipline compounds.
      </h2>
      <p className="body-lg">
        Clear reporting builds confidence. Structured growth protects margins. 
        Proactive planning creates stability. The businesses that thrive are those 
        built on financial foundations that scale with ambition.
      </p>
    </div>
  </section>
);

// Final CTA Section
const FinalCTASection = () => (
  <section data-testid="final-cta-section" className="bg-obsidian section-padding">
    <div className="max-w-4xl mx-auto text-center">
      <h2 className="heading-lg text-parchment mb-8">
        Build your finances on structure.
      </h2>
      <div className="flex flex-col sm:flex-row gap-4 justify-center">
        <a 
          href={HUBSPOT_MEETING_LINK}
          target="_blank"
          rel="noopener noreferrer"
          className="bg-parchment text-obsidian px-8 py-4 font-inter font-medium uppercase tracking-wider text-sm inline-flex items-center justify-center gap-2 hover:bg-white transition-colors duration-200"
          data-testid="final-cta-primary"
        >
          Schedule Your Strategy Call
          <ArrowRight size={16} strokeWidth={1.5} />
        </a>
        <a 
          href="/year-end-prep-guide.pdf" 
          download
          className="bg-transparent text-parchment px-8 py-4 font-inter font-medium uppercase tracking-wider text-sm border border-parchment inline-flex items-center justify-center hover:bg-parchment hover:text-obsidian transition-colors duration-200"
          data-testid="final-cta-secondary"
        >
          Financial Year End Prep Guide
        </a>
      </div>
    </div>
  </section>
);

// Header/Navigation
const Header = () => (
  <header className="fixed top-0 left-0 right-0 bg-parchment/95 backdrop-blur-sm z-50 border-b border-iron-grey">
    <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24 py-4 flex items-center justify-between">
      <Link to="/" className="font-bold text-obsidian tracking-wide" style={{ fontFamily: "'Glacial Indifference', sans-serif", fontSize: '16pt' }} data-testid="logo">
        OAKMERE ADVISORY
      </Link>
      <nav className="hidden md:flex items-center gap-8">
        <a href="#services" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors">Services</a>
        <a href="#pricing" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors">Pricing</a>
        <Link to="/login" className="font-inter text-sm text-obsidian hover:text-graphite transition-colors" data-testid="nav-login">
          Client Login
        </Link>
        <a href={HUBSPOT_MEETING_LINK} target="_blank" rel="noopener noreferrer" className="btn-primary text-xs py-2 px-4" data-testid="nav-cta">
          Book a Call
        </a>
      </nav>
      <Link to="/login" className="md:hidden font-inter text-sm text-obsidian" data-testid="nav-login-mobile">
        Login
      </Link>
    </div>
  </header>
);

// Footer
const Footer = () => (
  <footer className="bg-parchment border-t border-iron-grey py-12 px-6 md:px-12 lg:px-24">
    <div className="max-w-7xl mx-auto">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-8 mb-8">
        <div>
          <span className="text-[16px] font-bold text-obsidian tracking-widest" style={{ fontFamily: "'Glacial Indifference', sans-serif" }}>
            OAKMERE ADVISORY
          </span>
          <p className="font-inter text-sm text-iron-grey mt-2">
            Structured accounting for the digital economy.
          </p>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Services</span>
          <ul className="space-y-2">
            {['Accounting Systems', 'Tax Planning', 'Growth Advisory', 'Crypto Accounting'].map((item) => (
              <li key={item}>
                <a href="#services" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">{item}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Company</span>
          <ul className="space-y-2">
            {['About', 'Pricing', 'Contact'].map((item) => (
              <li key={item}>
                <a href={`#${item.toLowerCase()}`} className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">{item}</a>
              </li>
            ))}
          </ul>
        </div>
        <div>
          <span className="font-inter text-sm uppercase tracking-wider text-obsidian mb-4 block">Portal</span>
          <ul className="space-y-2">
            <li>
              <Link to="/login" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Client Login</Link>
            </li>
            <li>
              <Link to="/register" className="font-inter text-sm text-iron-grey hover:text-obsidian transition-colors">Create Account</Link>
            </li>
          </ul>
        </div>
      </div>
      <div className="border-t border-iron-grey pt-8 flex flex-col md:flex-row justify-between items-center gap-4">
        <p className="font-inter text-xs text-iron-grey">
          © {new Date().getFullYear()} Oakmere Advisory. All rights reserved.
        </p>
        <div className="flex gap-6">
          <a href="#privacy" className="font-inter text-xs text-iron-grey hover:text-obsidian transition-colors">Privacy Policy</a>
          <a href="#terms" className="font-inter text-xs text-iron-grey hover:text-obsidian transition-colors">Terms of Service</a>
        </div>
      </div>
    </div>
  </footer>
);

// Contact Modal
const ContactModal = ({ open, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    service_interest: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, formData);
      toast.success('Message sent successfully. We\'ll be in touch soon.');
      onClose();
      setFormData({ name: '', email: '', company: '', service_interest: '', message: '' });
    } catch (error) {
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-parchment border-iron-grey max-w-md" data-testid="contact-modal">
        <DialogHeader>
          <DialogTitle className="font-fraunces font-bold text-2xl text-obsidian">Get in Touch</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div>
            <Input 
              placeholder="Your Name" 
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              required
              className="bg-white border-iron-grey text-obsidian placeholder:text-iron-grey"
              data-testid="contact-name"
            />
          </div>
          <div>
            <Input 
              type="email"
              placeholder="Email Address" 
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="bg-white border-iron-grey text-obsidian placeholder:text-iron-grey"
              data-testid="contact-email"
            />
          </div>
          <div>
            <Input 
              placeholder="Company (optional)" 
              value={formData.company}
              onChange={(e) => setFormData({ ...formData, company: e.target.value })}
              className="bg-white border-iron-grey text-obsidian placeholder:text-iron-grey"
              data-testid="contact-company"
            />
          </div>
          <div>
            <Select 
              value={formData.service_interest} 
              onValueChange={(value) => setFormData({ ...formData, service_interest: value })}
            >
              <SelectTrigger className="bg-white border-iron-grey text-obsidian" data-testid="contact-service">
                <SelectValue placeholder="Service Interest" />
              </SelectTrigger>
              <SelectContent className="bg-white border-iron-grey">
                <SelectItem value="accounting">Accounting Systems & Reporting</SelectItem>
                <SelectItem value="tax">Tax Planning & Compliance</SelectItem>
                <SelectItem value="advisory">Growth & Profit Advisory</SelectItem>
                <SelectItem value="crypto">Digital Asset & Crypto Accounting</SelectItem>
                <SelectItem value="other">Other / General Inquiry</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Textarea 
              placeholder="Tell us about your needs..." 
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              className="bg-white border-iron-grey text-obsidian placeholder:text-iron-grey resize-none"
              data-testid="contact-message"
            />
          </div>
          <Button 
            type="submit" 
            disabled={loading}
            className="w-full btn-primary"
            data-testid="contact-submit"
          >
            {loading ? 'Sending...' : 'Send Message'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Main HomePage Component
const HomePage = () => {
  const [contactOpen, setContactOpen] = useState(false);

  return (
    <div className="min-h-screen bg-parchment">
      <Header />
      <main className="pt-16">
        <HeroSection />
        <PositioningStrip />
        <DifferentiationSection />
        <ServicesSection />
        <ClientExperienceSection />
        <ProcessSection />
        <PricingSection />
        <AuthoritySection />
        <FinalCTASection />
      </main>
      <Footer />
      <ContactModal open={contactOpen} onClose={() => setContactOpen(false)} />
    </div>
  );
};

export default HomePage;
